// Inputs: fields with labels, drop-down lists, switches and quiz answer options.
import { type ComponentProps, type ReactNode, useId } from 'react';
import { cx } from '@/lib/cx';
import { Icon } from './Icon';

type FieldProps = {
  label: string;
  /** Keep the label for screen readers only, when the page already says what to type. */
  hideLabel?: boolean;
  hint?: ReactNode;
  /** Renders the input; receives the id that links it to the label. */
  children: (id: string) => ReactNode;
};

/** Label, input and hint, linked for screen readers. */
export function Field({ label, hideLabel, hint, children }: FieldProps) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id} className={hideLabel ? 'sr-only' : undefined}>
        {label}
      </label>
      {children(id)}
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}

/**
 * A drop-down list with the kit's own arrow, so it sits the same in every browser. Full width, like an input;
 * `className` (on the wrapper) can make it as wide as its longest option instead.
 */
export function Select({ className, children, ...props }: ComponentProps<'select'>) {
  return (
    <span className={cx('select-wrap', className)}>
      <select className="select" {...props}>
        {children}
      </select>
      <Icon name="chevron-down" />
    </span>
  );
}

type SwitchRowProps = {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
};

/** A setting you turn on or off, with its label on the left. */
export function SwitchRow({ label, hint, checked, onChange }: SwitchRowProps) {
  return (
    <label className="between">
      <span>
        <span className="label">{label}</span>
        {hint && (
          <span className="hint" style={{ display: 'block' }}>
            {hint}
          </span>
        )}
      </span>
      <input
        className="switch"
        type="checkbox"
        role="switch"
        aria-checked={checked}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
    </label>
  );
}

type OptionProps = {
  /** A, B, C… */
  letter: string;
  children: ReactNode;
  selected?: boolean;
  /** After checking: right or wrong. */
  result?: 'right' | 'wrong' | undefined;
  onSelect?: () => void;
};

/** One answer in a quiz. */
export function Option({ letter, children, selected, result, onSelect }: OptionProps) {
  return (
    <button className={cx('option', result)} type="button" aria-pressed={selected ?? false} onClick={onSelect}>
      <span className="key">{letter}</span>
      <span className="grow">{children}</span>
      {result === 'right' && <Icon name="check" label="Right" />}
      {result === 'wrong' && <Icon name="x" label="Wrong" />}
    </button>
  );
}

/** Numbered step in an exercise (1, 2, 3). */
export function QuestionHead({ n, children }: { n: number; children: ReactNode }) {
  return (
    <div className="q-head">
      <span className="q-num">{n}</span>
      <h3>{children}</h3>
    </div>
  );
}
